import { useState, useCallback, useMemo } from 'react';
import { ESSENTIAL_EIGHT_CATEGORIES, ESSENTIAL_EIGHT_QUESTIONNAIRE } from '../constants/questions';
import { Answer, CategoryResponse, MaturityLevel, E8Assessment } from '../types';

export const useQuestionnaire = (tenantId: string) => {
  // Initialize responses for all categories
  const [responses, setResponses] = useState<Record<string, CategoryResponse>>(() => {
    // Create initial state with null answers for all categories
    const initialResponses: Record<string, CategoryResponse> = {};
    ESSENTIAL_EIGHT_CATEGORIES.forEach(category => {
      initialResponses[category.id] = {
        categoryId: category.id,
        responses: {
          partA: null,
          partB: null,
          partC: null
        },
        maturityLevel: 0
      };
    });
    return initialResponses;
  });

  // Handle answer changes
  const handleAnswerChange = useCallback((questionId: string, answer: Answer) => {
    // Find the corresponding category and question part
    let categoryId = '';
    let part: 'A' | 'B' | 'C' = 'A';

    ESSENTIAL_EIGHT_CATEGORIES.forEach(category => {
      category.questions.forEach(question => {
        if (question.id === questionId) {
          categoryId = category.id;
          part = question.part;
        }
      });
    });

    if (categoryId) {
      setResponses(prev => {
        // Create a copy of the previous state
        const newResponses = { ...prev };
        const categoryResponse = { ...newResponses[categoryId] };
        
        // Update the answer for the specific part
        categoryResponse.responses = {
          ...categoryResponse.responses,
          [`part${part}`]: answer
        };

        // Reset next parts if current answer is not "Yes"
        if (answer !== 'Yes') {
          if (part === 'A') {
            categoryResponse.responses.partB = null;
            categoryResponse.responses.partC = null;
          } else if (part === 'B') {
            categoryResponse.responses.partC = null;
          }
        }

        // Calculate maturity level based on answers
        const maturityLevel = calculateMaturityLevel(categoryResponse.responses);
        categoryResponse.maturityLevel = maturityLevel;

        // Update the category in the responses object
        newResponses[categoryId] = categoryResponse;
        return newResponses;
      });
    }
  }, []);

  // Calculate maturity level for a category based on answers
  const calculateMaturityLevel = (
    categoryResponses: { partA: Answer; partB: Answer; partC: Answer }
  ): MaturityLevel => {
    // Level 3 - All parts answered Yes
    if (
      categoryResponses.partA === 'Yes' &&
      categoryResponses.partB === 'Yes' &&
      categoryResponses.partC === 'Yes'
    ) {
      return 3;
    }
    
    // Level 2 - Parts A and B answered Yes
    if (
      categoryResponses.partA === 'Yes' &&
      categoryResponses.partB === 'Yes'
    ) {
      return 2;
    }
    
    // Level 1 - Part A answered Yes
    if (categoryResponses.partA === 'Yes') {
      return 1;
    }
    
    // Level 0 - No maturity
    return 0;
  };

  // Calculate overall maturity percentages
  const overallMaturity = useMemo(() => {
    const categoryCount = ESSENTIAL_EIGHT_CATEGORIES.length;
    let levelZeroCount = 0;
    let levelOneCount = 0;
    let levelTwoCount = 0;
    let levelThreeCount = 0;

    Object.values(responses).forEach(categoryResponse => {
      switch (categoryResponse.maturityLevel) {
        case 0:
          levelZeroCount++;
          break;
        case 1:
          levelOneCount++;
          break;
        case 2:
          levelTwoCount++;
          break;
        case 3:
          levelThreeCount++;
          break;
      }
    });

    return {
      levelZero: Math.round((levelZeroCount / categoryCount) * 100),
      levelOne: Math.round((levelOneCount / categoryCount) * 100),
      levelTwo: Math.round((levelTwoCount / categoryCount) * 100),
      levelThree: Math.round((levelThreeCount / categoryCount) * 100)
    };
  }, [responses]);

  // Get the assessment data
  const assessment: E8Assessment = useMemo(() => ({
    tenantId,
    timestamp: new Date(),
    responses,
    overallMaturity,
    // Add questionnaire version information
    questionnaireVersion: ESSENTIAL_EIGHT_QUESTIONNAIRE.version
  }), [tenantId, responses, overallMaturity]);

  return {
    categories: ESSENTIAL_EIGHT_CATEGORIES,
    responses,
    handleAnswerChange,
    assessment
  };
};

export default useQuestionnaire;
